import { formatNumber } from "@angular/common"
import { Injectable } from "@angular/core";
import { ExternalSettings } from "src/app/common/services/external-settings/external-settings.interface";
import { MeFormatValidation } from "src/app/common/components/me-validation/me-format-validation";
import { TranslateService } from "@ngx-translate/core";
import { MeValidation } from "src/app/common/components/me-validation/me-validation";

@Injectable({
  providedIn: 'root'
})
export class CurrencyUtilityService {

  private static maskingType: CurrencyMaskingType;
  private static translate: TranslateService;

  constructor() { }

  /**
   * Initialize via ExternalSettingsService.initializeLibaries
   */
  static init(externalSettings: ExternalSettings, translate: TranslateService) {
    this.translate = translate;
    this.maskingType = externalSettings.globalVariables.currencyMask?.endsWith('0.00') ? CurrencyMaskingType.DECIMAL_POINT : CurrencyMaskingType.DECIMAL_COMMA;
  }

  /**
   * For Displaying the amount with currency symbol
   */
  static formatAmount(code: string, amount: number | string): string {
    if (typeof amount == 'string') {
      return `${CurrencyUtilityService.getISOCurrencySymbol(code)} ${amount}`;
    } else {
      let locale = CurrencyUtilityService.maskingType == CurrencyMaskingType.DECIMAL_POINT ? "en-US" : "de-DE";
      return `${CurrencyUtilityService.getISOCurrencySymbol(code)} ${formatNumber(+amount, locale, '1.2-2')}`;
    }
  }

  /**
   * For Displaying the amount without currency symbol
   */
  static formatAmountNoSymbol(amount: number | string): string {
    if (amount == null) return '';

    let tmp: number = (typeof amount == 'string') ? Number(amount) : amount;
    let locale = CurrencyUtilityService.maskingType == CurrencyMaskingType.DECIMAL_POINT ? "en-US" : "de-DE";
    let result = formatNumber(tmp, locale, '1.2-2')
    return result;
  }

  /**
   * Used for displaying the amount back into Input Field, trimmed the thoushand separator
   * e.g. 8888.99 --> 8888,99
   * If using formatAmountNoSymbol alone, the result will become 8.888,99 which is invalid input field
   */
  static formatInputAmount(amount: number | string): string {
    let formated = CurrencyUtilityService.formatAmountNoSymbol(amount);
    if (CurrencyUtilityService.maskingType == CurrencyMaskingType.DECIMAL_COMMA) {
      formated = formated.replace('.', '');
    }
    else {
      formated = formated.replace(',', '');
    }
    return formated;
  }

  /**
   * Used for displaying the amount on the review screen fields, where amount/fee/total fields are processed by:
   * (1) the normalize() in the findBrickValue() --> MeInputNumberBrick.getValue() for the amount field
   * (2) the normalize(brick.value) in the replaceGetReviewDisplay(value: string){}, brick.value is not normalized
   * If using formatAmount alone, the decimal will be missing for string integer amount or in a wrong locale
   * If using formatAmountNoSymbol to handle non-normalized data ("e.g. 5,15"), the field will display infinity
   * Need to use formatAmountNoSymbol to get the right locale for the type number/normalized string
   * Then use formatAmount to attach the currency symbol
   */
  static formatAmountAfterNormalize(currencyCode: string, amount: number | string): string {
    if (!amount) return '';
    return CurrencyUtilityService.formatAmount(currencyCode, CurrencyUtilityService.formatAmountNoSymbol(amount));
  }

  // Normalize the Value before sending to backend
  static normalize(amount: number | string): string {

    if (typeof amount == 'number') {
      return amount.toString();
    }

    if (CurrencyUtilityService.maskingType == CurrencyMaskingType.DECIMAL_COMMA) {
      amount = amount.replace('.', '');
      amount = amount.replace(',', '.');
    }
    else {
      amount = amount.replace(',', '');
    }
    return amount;
  }

  static normalizeNumber(amount: number | string): number {
    return Number(CurrencyUtilityService.normalize(amount));
  }

  static normalizeStringOptional(amount: number | string | null, defaultValue: string | null = null): string | null {
    if (amount == null) return defaultValue;
    return CurrencyUtilityService.normalize(amount);
  }

  static normalizeNumberOptinal(amount: number | string | null, defaultValue: number | null = null): number | null {
    if (amount == null) return defaultValue;
    return CurrencyUtilityService.normalizeNumber(amount);
  }

  static getCurrencyCharacters(): { decimalCharacter: string; groupingCharacter: string; } {
    if (CurrencyUtilityService.maskingType == CurrencyMaskingType.DECIMAL_POINT) {
      return { decimalCharacter: '.', groupingCharacter: ',' }
    } else {
      return { decimalCharacter: ',', groupingCharacter: '.' }
    }
  }

  static getISOCurrencySymbol(currencyCode: string): string {
    return ISOAlphaCurrencySymbolMap.get(currencyCode) ?? '';
    // if (currencyCode in ISOAlphaCurrencySymbol) {
    //   return ISOAlphaCurrencySymbol[currencyCode as keyof typeof ISOAlphaCurrencySymbol];
    // } else {
    //   return ''
    // }
  }

  static readonly getReviewDisplay = CurrencyUtilityService.getSummaryDisplay;
  static getSummaryDisplay(value: string): string {
    if (value == null) return '';
    let denormlaized = CurrencyUtilityService.normalize(value);
    return CurrencyUtilityService.formatAmountNoSymbol(denormlaized);
  }

  static getValidations(errorKey: string = 'MESSAGE_ERROR_FORMAT'): [MeValidation] {
    let { pattern, readablePattern } = CurrencyUtilityService.getValidationPattern();
    return [new MeFormatValidation(CurrencyUtilityService.translate, pattern, errorKey, readablePattern, false)];
  }

  private static getValidationPattern(): { pattern: any; readablePattern: any; placeHolder: any } {
    switch (CurrencyUtilityService.maskingType) {
      case CurrencyMaskingType.DECIMAL_POINT:
        return {
          pattern: CurrencyMaskingPattern.DECIMAL_POINT,
          readablePattern: CurrencyMaskingReadablePattern.DECIMAL_POINT,
          placeHolder: CurrencyMaskPlaceholder.DECIMAL_POINT,
        }
      case CurrencyMaskingType.DECIMAL_COMMA:
        return {
          pattern: CurrencyMaskingPattern.DECIMAL_COMMA,
          readablePattern: CurrencyMaskingReadablePattern.DECIMAL_COMMA,
          placeHolder: CurrencyMaskPlaceholder.DECIMAL_COMMA,
        }
    }
  }

  static getDecimalValidations(decimals: number, errorKey: string = 'MESSAGE_ERROR_FORMAT'): [MeValidation] {
    let { pattern, readablePattern } = CurrencyUtilityService.getDecimalValidationPattern(decimals);
    return [new MeFormatValidation(CurrencyUtilityService.translate, pattern, errorKey, readablePattern, false)];
  }

  static getPlaceholder(decimals: number = 2) {
    return CurrencyUtilityService.maskingType == CurrencyMaskingType.DECIMAL_POINT ? '0.'+'0'.repeat(decimals) : '0,'+'0'.repeat(decimals);
  }

  private static getDecimalValidationPattern(decimals: number = 2): { pattern: any; readablePattern: any;} {
    switch (CurrencyUtilityService.maskingType) {
      case CurrencyMaskingType.DECIMAL_POINT:
        return {
          pattern: `^\\d+(\\.\\d{1,${decimals}})?$`,
          readablePattern: '###.'+'#'.repeat(decimals),
        }
      case CurrencyMaskingType.DECIMAL_COMMA:
        return {
          pattern: `^\\d+(,\\d{1,${decimals}})?$`,
          readablePattern: '###,'+'#'.repeat(decimals),
        }
    }
  }

}

enum CurrencyMaskingType {
  DECIMAL_POINT = 'DECIMAL_POINT', // 123.45
  DECIMAL_COMMA = 'DECIMAL_COMMA'  // 123,45
}

export enum CurrencyMaskingPattern {
  DECIMAL_POINT = '^\\d+(\\.\\d{1,2})?$',
  DECIMAL_COMMA = '^\\d+(,\\d{1,2})?$'
}

export enum CurrencyMaskingReadablePattern {
  DECIMAL_POINT = '###.##',
  DECIMAL_COMMA = '###,##'
}

export enum CurrencyMaskPlaceholder {
  DECIMAL_POINT = '0.00',
  DECIMAL_COMMA = '0,00'
}

export interface Currency {
  currencyCode: string,
  isoCurrencyCode: string,
  symbol: string,
  numericCode: number
}

export enum ISOCurrencyCode {
  USD = "USD",
  XCD = "XCD",
  ECD = "ECD"
}

// export enum ISOAlphaCurrencySymbol {
//   EUR = "€",
//   ECU = "₠",
//   GBP = "£",
//   ECD = "EC$",
//   XCD = "EC$",
//   USD = "US$",
//   CAD = "CA$",
//   BZE = "BZ$",
//   BZD = "BZ$",
//   SRD = "SR$",
//   RUB = "₽",
//   UAH = "₴",
// }

const ISOAlphaCurrencySymbolMap = new Map<string, string>([
  ["EUR", "€"],
  ["ECU", "₠"],
  ["GBP", "£"],
  ["ECD", "EC$"],
  ["XCD", "EC$"],
  ["USD", "US$"],
  ["CAD", "CA$"],
  ["BZE", "BZ$"],
  ["BZD", "BZ$"],
  ["SRD", "SR$"],
  ["RUB", "₽"],
  ["UAH", "₴"],
]);

